import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { SpaRequestStatus } from '@erp/shared-types';

export interface SpaRecordsListOptions {
  search?: string;
  vendor?: string;
  productNumber?: string;
  validFrom?: string;
  validTo?: string;
  limit?: number;
  offset?: number;
}

export interface SpaRequestsListOptions {
  status?: SpaRequestStatus;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useSpaRequestsList(options: SpaRequestsListOptions = {}) {
  const input = {
    ...(options.status ? { status: options.status } : {}),
    ...(options.search ? { search: options.search } : {}),
    limit: options.limit ?? 100,
    offset: options.offset ?? 0,
  };

  return useQuery(
    orpc.pricing.spaRequests.list.queryOptions({
      input,
    }),
  );
}

export function useSpaRequest(id: string | undefined) {
  return useQuery({
    ...orpc.pricing.spaRequests.get.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: Boolean(id),
  });
}

export function useSpaRecord(id: string | undefined) {
  return useQuery({
    ...orpc.pricing.spas.get.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: Boolean(id),
  });
}

export function useSpaRecordsList(options: SpaRecordsListOptions = {}) {
  const input = {
    ...(options.search ? { search: options.search } : {}),
    ...(options.vendor ? { vendor: options.vendor } : {}),
    ...(options.productNumber ? { productNumber: options.productNumber } : {}),
    ...(options.validFrom ? { validFrom: options.validFrom } : {}),
    ...(options.validTo ? { validTo: options.validTo } : {}),
    limit: options.limit ?? 100,
    offset: options.offset ?? 0,
  };

  return useQuery(
    orpc.pricing.spas.list.queryOptions({
      input,
    }),
  );
}

export function useInvalidateSpaRecords() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    void queryClient.invalidateQueries({
      queryKey: orpc.pricing.spas.list.queryOptions({ input: {} }).queryKey,
    });
    if (id) {
      void queryClient.invalidateQueries({
        queryKey: orpc.pricing.spas.get.queryOptions({
          input: { id },
        }).queryKey,
      });
    }
  };
}

/** Invalidate both the list and the specific item after a mutation. */
export function useInvalidateSpaRequests() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    void queryClient.invalidateQueries({
      queryKey: orpc.pricing.spaRequests.list.queryOptions({ input: {} })
        .queryKey,
    });
    if (id) {
      void queryClient.invalidateQueries({
        queryKey: orpc.pricing.spaRequests.get.queryOptions({
          input: { id },
        }).queryKey,
      });
    }
  };
}
